"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { clsx } from "clsx";

type ToastType = "success" | "error" | "info";

type ToastItem = {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
};

type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
};

type AlertOptions = {
  title: string;
  message: string;
  okLabel?: string;
};

type UiFeedbackContextValue = {
  showToast: (opts: { type?: ToastType; message: string; duration?: number }) => void;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
};

const UiFeedbackContext = createContext<UiFeedbackContextValue | null>(null);

export function useToast() {
  const ctx = useContext(UiFeedbackContext);
  if (!ctx) throw new Error("useToast must be used within UiFeedbackProvider");
  return { showToast: ctx.showToast };
}

export function useConfirm() {
  const ctx = useContext(UiFeedbackContext);
  if (!ctx) throw new Error("useConfirm must be used within UiFeedbackProvider");
  return ctx.confirm;
}

export function useAlert() {
  const ctx = useContext(UiFeedbackContext);
  if (!ctx) throw new Error("useAlert must be used within UiFeedbackProvider");
  return ctx.alert;
}

function ToastIcon({ type }: { type: ToastType }) {
  const className = "size-5 shrink-0";
  if (type === "success") return <CheckCircle2 className={clsx(className, "text-[var(--success)]")} />;
  if (type === "error") return <AlertCircle className={clsx(className, "text-[var(--destructive)]")} />;
  return <Info className={clsx(className, "text-[var(--primary)]")} />;
}

function ToastStack({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: string) => void }) {
  if (!toasts.length) return null;
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2 p-4 sm:bottom-6 sm:right-6"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={clsx(
            "pointer-events-auto flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-[var(--shadow-md)] toast-in",
            toast.type === "success" && "border-emerald-200 bg-white",
            toast.type === "error" && "border-red-200 bg-white",
            toast.type === "info" && "border-[var(--card-border)] bg-[var(--card)]",
          )}
        >
          <ToastIcon type={toast.type} />
          <p className="min-w-0 flex-1 text-sm leading-relaxed text-[var(--foreground)]">{toast.message}</p>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            className="shrink-0 rounded-lg p-1 text-[var(--muted)] hover:bg-[var(--muted-bg)] hover:text-[var(--foreground)]"
            aria-label="ปิด"
          >
            <X className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function MessageDialog({
  title,
  message,
  icon,
  actions,
  onClose,
}: {
  title: string;
  message: string;
  icon: ReactNode;
  actions: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ui-dialog-title"
        className="w-full max-w-md rounded-2xl border border-[var(--card-border)] bg-[var(--card)] p-6 shadow-[var(--shadow-md)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-4">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--muted-bg)]">{icon}</div>
          <div className="min-w-0 flex-1">
            <h2 id="ui-dialog-title" className="text-lg font-semibold text-[var(--foreground)]">
              {title}
            </h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[var(--muted)]">{message}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">{actions}</div>
      </div>
    </div>
  );
}

export function UiFeedbackProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const [alertState, setAlertState] = useState<(AlertOptions & { resolve: () => void }) | null>(null);
  const timersRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    ({ type = "info", message, duration = 4000 }: { type?: ToastType; message: string; duration?: number }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((prev) => [...prev, { id, type, message, duration }]);
      const timer = window.setTimeout(() => dismissToast(id), duration);
      timersRef.current.set(id, timer);
    },
    [dismissToast],
  );

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...opts, resolve });
    });
  }, []);

  const alert = useCallback((opts: AlertOptions) => {
    return new Promise<void>((resolve) => {
      setAlertState({ ...opts, resolve });
    });
  }, []);

  const closeConfirm = (result: boolean) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  const closeAlert = () => {
    alertState?.resolve();
    setAlertState(null);
  };

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!confirmState && !alertState) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (confirmState) closeConfirm(false);
        else closeAlert();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmState, alertState]);

  return (
    <UiFeedbackContext.Provider value={{ showToast, confirm, alert }}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
      {confirmState ? (
        <MessageDialog
          title={confirmState.title}
          message={confirmState.message}
          icon={
            confirmState.variant === "destructive" ? (
              <AlertTriangle className="size-5 text-[var(--destructive)]" />
            ) : (
              <Info className="size-5 text-[var(--primary)]" />
            )
          }
          onClose={() => closeConfirm(false)}
          actions={
            <>
              <button
                type="button"
                onClick={() => closeConfirm(false)}
                className="rounded-xl border border-[var(--card-border)] px-4 py-2.5 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted-bg)]"
              >
                {confirmState.cancelLabel ?? "ยกเลิก"}
              </button>
              <button
                type="button"
                onClick={() => closeConfirm(true)}
                className={clsx(
                  "rounded-xl px-4 py-2.5 text-sm font-medium text-white",
                  confirmState.variant === "destructive"
                    ? "bg-[var(--destructive)] hover:opacity-90"
                    : "bg-[var(--primary)] hover:bg-[var(--primary-hover)]",
                )}
              >
                {confirmState.confirmLabel ?? "ยืนยัน"}
              </button>
            </>
          }
        />
      ) : null}
      {alertState ? (
        <MessageDialog
          title={alertState.title}
          message={alertState.message}
          icon={<CheckCircle2 className="size-5 text-[var(--success)]" />}
          onClose={closeAlert}
          actions={
            <button
              type="button"
              onClick={closeAlert}
              className="rounded-xl bg-[var(--primary)] px-4 py-2.5 text-sm font-medium text-[var(--primary-foreground)] hover:bg-[var(--primary-hover)]"
            >
              {alertState.okLabel ?? "ตกลง"}
            </button>
          }
        />
      ) : null}
    </UiFeedbackContext.Provider>
  );
}
